/**
 * Inline transcript footers (WS6): per-turn cost footer + stream error note.
 * (The knowledge proposal/reflection cards left with the knowledge system —
 * DECISIONS #62; persisted proposal frames replay as nothing.)
 */

import { useState } from 'react';

import { useMe } from '../../api/queries';
import type { Usage } from '../../api/types';
import {
  formatTokenCount,
  formatTurnCost,
  formatUsdDetailed,
  hasCacheTraffic,
  turnCostBreakdown,
} from './pricing';

/** "39.5k in · 1.1k out" — the token detail line (grandma audit #12). */
export function formatTokenDetail(usage: Usage, model?: string | null): string {
  const fmt = (n: number) => (n < 1000 ? String(n) : `${(n / 1000).toFixed(1)}k`);
  const tokens = `${fmt(usage.input_tokens)} tokens in · ${fmt(usage.output_tokens)} out`;
  return model !== null && model !== undefined ? `${tokens} · model: ${model}` : tokens;
}

/**
 * The cost info popover (owner round 2026-08-25 item 5: hovering the ⓘ
 * previously showed nothing). A real, owned tooltip: model name, exact
 * input/output token counts, and the price math, opened by hover AND by
 * click/keyboard (the button is focusable; Enter/Space pin it, Escape
 * closes). Exported for tests.
 */
export function TurnCostInfo({
  usage,
  model,
  showDollars,
}: {
  usage: Usage;
  model?: string | null;
  /** false in keyless/fake mode: the math still shows, with a nothing-billed note */
  showDollars: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;
  const breakdown = turnCostBreakdown(model, usage);
  const close = () => {
    setHovered(false);
    setPinned(false);
  };
  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        data-testid="turn-usage-info"
        aria-label="What this turn used"
        aria-expanded={open}
        onFocus={() => setHovered(true)}
        onBlur={close}
        onClick={() => setPinned((p) => !p)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close();
        }}
        className="cursor-pointer border-none bg-transparent p-0 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-inherit"
      >
        ⓘ
      </button>
      {open ? (
        <div
          role="tooltip"
          data-testid="turn-usage-tooltip"
          className="absolute bottom-full left-0 z-30 mb-1 w-max rounded-card border border-default bg-raised p-2 text-left font-(family-name:--font-ui) text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] whitespace-nowrap text-secondary shadow-(--shadow-overlay)"
        >
          <div className="mono text-primary">{model ?? 'model not recorded'}</div>
          <div>{formatTokenDetail(usage)}</div>
          {breakdown !== null ? (
            <>
              {hasCacheTraffic(usage) ? (
                // A cached turn does not price as one number times one rate,
                // and showing it that way is what put a wrong dollar figure
                // under every answer. Three lines, because there are three
                // rates: fresh input, storing it, reading it back.
                <>
                  <div data-testid="turn-usage-fresh">
                    New: {formatTokenCount(breakdown.uncachedInputTokens)} × $
                    {breakdown.price.inputPerMTok}/M ={' '}
                    {formatUsdDetailed(breakdown.uncachedInputUsd)}
                  </div>
                  {breakdown.cacheWriteTokens > 0 ? (
                    <div data-testid="turn-usage-cache-write">
                      Saved for reuse: {formatTokenCount(breakdown.cacheWriteTokens)} at 1.25x ={' '}
                      {formatUsdDetailed(breakdown.cacheWriteUsd)}
                    </div>
                  ) : null}
                  {breakdown.cacheReadTokens > 0 ? (
                    <div data-testid="turn-usage-cache-read">
                      Reused from before: {formatTokenCount(breakdown.cacheReadTokens)} at a tenth
                      of the rate = {formatUsdDetailed(breakdown.cacheReadUsd)}
                    </div>
                  ) : null}
                </>
              ) : (
                <div>
                  In: {formatTokenCount(usage.input_tokens)} × $
                  {breakdown.price.inputPerMTok}/M = {formatUsdDetailed(breakdown.inputUsd)}
                </div>
              )}
              <div>
                Out: {formatTokenCount(usage.output_tokens)} × $
                {breakdown.price.outputPerMTok}/M = {formatUsdDetailed(breakdown.outputUsd)}
              </div>
              <div className="text-primary">
                Total ≈ {formatUsdDetailed(breakdown.totalUsd)} (list price)
              </div>
            </>
          ) : (
            <div>No list price on file for this model.</div>
          )}
          {!showDollars ? <div>No API key active, so nothing was billed.</div> : null}
        </div>
      ) : null}
    </span>
  );
}

/**
 * Quiet per-turn cost footer (DESIGN2 §10.6 as re-cut by the grandma audit
 * #12: "64 out of what?"). The visible text is DOLLARS ONLY, and only when a
 * real key is active (no imaginary spend in fake/keyless mode); the token
 * counts, the model id, and the price math live behind the ⓘ popover
 * (TurnCostInfo).
 *
 * Round 5 turned the volume down. It used to arrive in the code face at 70%
 * opacity and brighten when the pointer crossed the turn — which is a way of
 * asking to be looked at, and a per-turn price is not the thing to look at.
 * Now it is tertiary UI text at one weight that never moves: there if you
 * want it, invisible if you do not. The session's running total, which is the
 * number a person actually watches, sits in the header instead.
 */
export function TurnUsage({ usage, model }: { usage: Usage; model?: string | null }) {
  const me = useMe();
  const cost = formatTurnCost(model, usage);
  const realKey = me.data?.api_key_present === true;
  return (
    <div
      data-testid="turn-usage"
      className="text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] text-tertiary"
    >
      {realKey && cost !== null ? `${cost} ` : ''}
      <TurnCostInfo usage={usage} model={model} showDollars={realKey} />
    </div>
  );
}

export function StreamErrorNote({
  message,
  onRetry,
}: {
  message: string;
  /** Re-sends the question this error answered; absent when a retry cannot help (a Stop, a bad key). */
  onRetry?: () => void;
}) {
  return (
    <div
      data-testid="stream-error"
      className="my-3 rounded-card border border-(--danger) bg-raised p-3 text-[length:var(--text-ui-size)] text-primary"
    >
      <div>{message}</div>
      {onRetry !== undefined ? (
        <button
          type="button"
          data-testid="turn-retry"
          onClick={onRetry}
          className="mt-2 rounded-md border border-(--border-strong) bg-surface px-3 py-1 text-[length:var(--text-ui-size)] font-medium text-primary hover:bg-raised"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}
